import Cookies from "js-cookie";
import React, { useEffect, useState, useReducer, useCallback } from "react";
import useAxios from "../../services/useAxios";
import { ApiResponse } from "../../types/loginAPi";
import { mobileChecker, nameChecker } from "../../utilities/regexCheckers";

import authReducer, {
  AuthActionType,
  UserType,
  UserRollType,
  AccessLevel,
} from "./AuthReducer";
import { useNavigate } from "react-router-dom";

interface AuthContextType {
  isLogin: boolean;
  isChecked: boolean;
  login: (theUserInfo: UserType) => void;
  logout: () => void;
  changeUserInfo: ({
    firstname = "",
    lastname = "",
  }: {
    firstname: string;
    lastname: string;
  }) => void;
  userInfo: UserType;
  refreshAddresses: () => Promise<void>;
}

const AuthenticationContext = React.createContext<AuthContextType>({
  isLogin: false,
  isChecked: false,
  userInfo: {
    id: 0,
    firstname: "",
    lastname: "",
    token: "",
    mobile: "",
    addresses: [],
    wallet: {
      id: 0,
      current_inventory: 0,
      main_status: 0,
      type: 0,
    },
    user_rolls: [],
    access_level: {
      full: [],
      codes: [],
      isSuperAdmin: false,
      supplierList: [],
    },
  },
  login: (theUserInfo) => {},
  logout: () => {},
  changeUserInfo: (theUserInfo) => {},
  refreshAddresses: async () => {},
});

export default AuthenticationContext;

const accessLevelListSuperAdmin = [
  {
    value: 100,
    name: "product-management",
  },
  {
    value: 101,
    name: "user-management",
  },
  {
    value: 102,
    name: "option-management",
  },
  {
    value: 103,
    name: "order-management",
  },
  {
    value: 104,
    name: "supplier-management",
  },
  {
    value: 105,
    name: "line-management",
  },
  {
    value: 106,
    name: "customer-club-management",
  },
  {
    value: 107,
    name: "finance-management",
  },
  {
    value: 108,
    name: "dashboard",
  },
];
const accessLevelListAdmin = [
  { value: 200, name: "setting" },
  { value: 201, name: "submit-product" },
  { value: 202, name: "basket" },
  { value: 203, name: "order" },
  { value: 204, name: "delivery" },
  { value: 205, name: "finance" },
];

const AuthenticationWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { execute: checkLoginInfoApi } = useAxios("api/checklogininfo");
  const { execute: logoutApi } = useAxios("api/logout");
  const { execute: findAddressesApi } = useAxios("api/Find_UsersAddress");

  const nav = useNavigate();

  const [userInfo, dispatch] = useReducer<typeof authReducer>(authReducer, {
    id: 0,
    firstname: "",
    lastname: "",
    token: "",
    mobile: "",
    addresses: [],
    wallet: {
      id: 0,
      current_inventory: 0,
      main_status: 0,
      type: 0,
    },
    user_rolls: [],
    access_level: {
      full: [],
      codes: [],
      isSuperAdmin: false,
      supplierList: [],
    },
  });

  const [isLogin, setIsLogin] = useState<boolean>(false);
  const [isChecked, setIsChecked] = useState<boolean>(false);

  const login = useCallback(
    (theUserInfo: UserType, isReload: boolean = false) => {
      dispatch({
        type: AuthActionType.LogIn,
        payload: theUserInfo,
      });
      setIsLogin(true);
      Cookies.set("usertoken", theUserInfo.token, { expires: 7 });
      console.log("kia-test", theUserInfo);
      console.log("kia-test", theUserInfo.adminpanelkey);
      if (theUserInfo.adminpanelkey) {
        console.log("kia-test", theUserInfo.supplier?.id);
        Cookies.set("adminpanelkey", theUserInfo.adminpanelkey, { expires: 7 });
        if (theUserInfo.supplier?.id && !isReload) {
          nav(`/admin-panel/${theUserInfo.supplier.id}`);
        } else {
          nav("/");
        }
      } else if (theUserInfo.access_level.isSuperAdmin && !isReload) {
        nav("/super-admin-panel");
      }
    },
    [dispatch, setIsLogin]
  );

  const logout = () => {
    logoutApi().then(
      (res: {
        status: number;
        errors?: string[];
        data: { usertoken: string };
      }) => {
        if (+res.status === 1) {
          dispatch({
            type: AuthActionType.LogOut,
          });
          Cookies.set("usertoken", res.data.usertoken, { expires: 7 });
          if (typeof Cookies.get("adminpanelkey") !== "undefined") {
            Cookies.remove("adminpanelkey");
          }
          setIsLogin(false);
          nav("/");
        }
      }
    );
  };

  const accessLevels = useCallback(
    (array: UserRollType[]) => {
      let fullArray: Array<{ value: string | number; name: string | number }> =
        [];
      let arrayValue: Array<string | number> = [];
      let adminList: Array<{
        id: string | number;
        title: string;
      }> = [];

      let result: AccessLevel = {
        full: fullArray,
        codes: arrayValue,
        isSuperAdmin: false,
        supplierList: [],
      };
      accessLevelListSuperAdmin.filter((b) =>
        array.find((s) => {
          if (s.value !== null && +b.value === +s.value) {
            fullArray.push(b);
            arrayValue.push(b.value);
          }
        })
      );
      array.map((v) => {
        if (
          v.supplier_id !== null &&
          !adminList.find((val) => +val.id === +v.supplier_id!)
        ) {
          adminList.push({
            id: v.supplier_id,
            title: v.title,
          });
        }
      });
      return {
        ...result,
        isSuperAdmin: result.full.length > 0 ? true : false,
        supplierList: adminList,
      };
    },
    [accessLevelListSuperAdmin]
  );

  const checkLoginInfo = useCallback(() => {
    checkLoginInfoApi().then((response: ApiResponse) => {
      if (+response.status === 1) {
        let {
          data,
          userinfo: { wallet, ...userinfo },
        } = response;
        login(
          {
            id: userinfo.id,
            firstname: userinfo.fname,
            lastname: userinfo.lname,
            mobile: userinfo.mobile,
            token: data.usertoken,
            adminpanelkey: data.adminpanelkey,
            addresses: userinfo.addresses,
            user_rolls: userinfo.user_rolls,
            supplier: userinfo.supplier,
            wallet: {
              id: wallet.id,
              current_inventory: wallet.current_inventory,
              main_status: wallet.main_status,
              type: wallet.type,
            },
            access_level: accessLevels(userinfo.user_rolls),
          },
          true
        );
      } else if (
        response?.data?.usertoken &&
        typeof Cookies.get("usertoken") === "undefined"
      ) {
        console.log(Cookies.get("usertoken"));
        Cookies.set("usertoken", response.data.usertoken, { expires: 7 });
      }
      setIsChecked(true);
    });
  }, [accessLevels, checkLoginInfoApi, setIsChecked, login]);

  const changeUserInfo = useCallback(
    ({
      firstname,
      lastname,
      mobile,
    }: {
      firstname?: string;
      lastname?: string;
      mobile?: string;
    }) => {
      let payload: {
        firstname?: string;
        lastname?: string;
        mobile?: string;
      } = {};

      if (typeof firstname !== "undefined" && nameChecker(firstname.trim())) {
        payload.firstname = firstname;
      }

      if (typeof lastname !== "undefined" && nameChecker(lastname.trim())) {
        payload.lastname = lastname;
      }
      if (typeof mobile !== "undefined" && mobileChecker(mobile.trim())) {
        payload.mobile = mobile;
      }

      if (Object.keys(payload).length !== 0) {
        dispatch({
          type: AuthActionType.ChangeInfo,
          payload,
        });
      }
    },
    [dispatch]
  );

  const refreshAddresses = useCallback(async () => {
    await findAddressesApi({
      conditions: `user_id/=/${userInfo.id}@@status/!=/-1`,
    }).then((res) => {
      dispatch({ type: AuthActionType.GetAddresses, payload: res });
    });
  }, [findAddressesApi, dispatch, userInfo.id]);

  useEffect(() => {
    checkLoginInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let state = {
    userInfo,
    isLogin,
    isChecked,
    login,
    logout,
    changeUserInfo,
    refreshAddresses,
  };

  return (
    <AuthenticationContext.Provider value={state}>
      {isChecked ? (
        <>{children}</>
      ) : (
        <div
          style={{
            width: "100vw",
            height: "100vh",
            // backgroundColor: "#999999",
          }}
        >
          {/* <div
            id="loading"
            style={{
              position: "fixed",
              left: "50%",
              top: "50%",

              transform: "translate(-50%, -50%)",
            }}
          >
            Loading...
          </div> */}
        </div>
      )}
    </AuthenticationContext.Provider>
  );
};

export { AuthenticationWrapper };
